import { ButtonType } from '@/components/CmgButton/cmg-button';
import { OfferTypeFilter, Reward } from '@/models';
import { AccountManagementEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

@Component({
  name: 'CmgRewardDetailModal',
})
export default class CmgRewardDetailModal extends Vue {
  hover = false;
  isRedeemingReward = false;

  get isUpdatedContextualRewardsBagUpdatesEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isUpdatedContextualRewardsBagUpdatesEnabled;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get rewardStarUrl(): string {
    return this.$services.reward.getRewardStarUrl(this.reward, this.hover);
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.rewardDetail.visible;
  }

  get args() {
    return this.$appStore.state.ui.modals.rewardDetail.args;
  }
  get pointsBalance(): number {
    return this.$services.reward.getPointsBalance();
  }

  get pointsBalanceClassObject() {
    return {
      'bold-brown': !this.pointsNeeded,
    };
  }

  get isFoodReward(): boolean {
    return this.reward.offerType == OfferTypeFilter.Food;
  }

  get pointsNeeded(): number {
    if (this.reward.points > this.pointsBalance) {
      return this.reward.points - this.pointsBalance;
    }

    return 0;
  }

  get reward(): Reward {
    return this.$appStore.state.ui.modals.rewardDetail.args!.reward;
  }

  get rewardDescription(): string {
    return `Get one ${ this.reward.title }${ this.canRedeemMultipleRewards ? '. Limit one reward per order' : 'with your next order' }.`;
  }

  get redeemText(): string {
    return `REDEEM FOR ${this.reward.points.toLocaleString()} POINTS`;
  }

  get canRedeemMultipleRewards() {
    return this.$appStore.state.reward.rewards[1].points < this.pointsBalance;
  }

  onClickRewardStar() {
    const action = this.reward.isFavorite ? AnalyticsEventAction.RewardUnfavorited : AnalyticsEventAction.RewardFavorited;
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardDetailModal,
      action: this.reward.title + action,
      label: AnalyticsEventLabel.RewardDetailModalSpecific + this.reward.title,
    });

    if (!this.reward.isFavorite) {
      this.$services.analytics.send(
        new AccountManagementEvent(InteractionTrackingType.RewardsFavorited, [], this.reward.title),
      );
    }
    this.$services.reward.toggleFavoriteStatus(this.reward);
  }

  onClickRedeem() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardDetailModal,
      action: this.reward.title + AnalyticsEventAction.RewardRedeemClick,
      label: AnalyticsEventLabel.RewardDetailModalSpecific + this.reward.title,
    });
    const reward = this.reward;
    this.isRedeemingReward = true;
    this.$services.ui.setAppPepperVisibility(true);
    this.$services.reward
      .purchaseReward(reward.id)
      .then(async purchasedReward => {
        if (purchasedReward) {
          if (this.args && this.args.onRedeem) {
            this.args?.onRedeem();
          } else {
            this.$services.ui.setModalVisibility('rewardRedemptionSuccess', true, {
              purchasedReward,
              reward
            });
            this.$services.analytics.send(
              new AccountManagementEvent(
                'rewardsAddedToWallet',
                undefined,
                reward.title,
                'account',
                purchasedReward.promotionCode,
                undefined,
                purchasedReward.promotionCampaign,
                undefined,
                'account:rewards:home',
                'favorited-reward-layer',
              )
            );
          }
        }
      })
      .catch(error => {
        if (this.$services.reward.isSparkflyError(error)) {
          this.$services.ui.setModalVisibility('rewardRedemptionSuccess', true, { reward, showErrorMessage: true });
          this.$services.reward.setShouldShowRefresh(true);
        } else {
          const onClose = () => {
            this.$services.ui.setModalVisibility('basicError', false);
            this.$services.ui.setModalVisibility('rewardDetail', true, { reward });
          };

          this.$services.googleAnalytics.sendCustomEvent({
            category: AnalyticsEventCategory.RewardDetailModal,
            action: AnalyticsEventAction.PurchaseError,
            label: AnalyticsEventLabel.PurchaseError,
          });

          this.$services.ui.setModalVisibility('basicError', true, {
            header: `OUR BAD`,
            content: 'There was a problem getting your reward. Give it a sec and try again.',
            background: 'white',
            options: [
              {
                text: 'Okay',
                type: ButtonType.Primary,
                onClick: onClose,
              },
            ],
            onClose,
          });
        }
      })
      .finally(() => {
        this.$services.loyalty.refreshPoints(true);
        this.$services.promotions.refreshCustomerPromotions();
        this.$services.ui.setAppPepperVisibility(false);
        this.isRedeemingReward = false;
        this.onClose();
      });
  }

  onClickRewardsExchange() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardDetailModal,
      action: AnalyticsEventAction.ReturnToRewardExchangeClick,
      label: AnalyticsEventLabel.ReturnToRewardExchangeClick,
    });
    this.onClose();
  }

  onClickTermsAndConditions(event: any) {
    const reward = this.reward;
    const onBack = () => {
      this.$services.ui.setModalVisibility('rewardDetail', true, { reward });
      this.$services.ui.setModalVisibility('rewardTermsAndConditions', false);
    };

    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: reward.terms, onBack });
    this.$services.ui.firstFocus();
    this.$services.ui.setModalVisibility('rewardDetail', false, { reward });
  }

  onClose() {
    this.$services.ui.setModalVisibility('rewardDetail', false, { reward: this.reward });
  }
}
